import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '../../../components/_base/Button';
import { MeetingSlot, PresenterGroupData, SpeakerProfile } from '../../../lib/api';
import BookMeetingModal from './BookMeetingModal';
import BookingResultModal from './BookingResultModal';
import Icon from '../../../components/_base/Icon';
import { getCurrentTimezone, dateToFormattedString, DateFnsFormats, formatTimeWithUppercasePeriod, getEventOffsetGMT, getIsEnUsRegion, TimeZoneDisplayTypeLabels, shouldShowLocalTime } from '../../../lib/helpers/dateHelper';
import { DateFilter } from '../SessionWrapper/DateFilter';
import { MAX_VISIBLE_DATE_PILLS } from '../SessionWrapper/dateFilterOverflow';
import { FormattedMeetingSlot, useMeetingDateSelector } from './useMeetingDateSelector';
import { utcToZonedTime } from 'date-fns-tz';
import { useTemplateProcessor } from '../../../hooks/use-template-processor';
import { BookableStatus, PRIMARY_COLOR } from '../../../lib/constants';
import { RegistrationResult, RegistrationResultType } from '../../../hooks/use-booking-subscription';
import { EventSettingsContextType } from '../../../lib/context-providers/event-settings-context/event-settings-context';
import { usePathPrefix } from '../../../hooks/use-path-prefix';
import { normalizeRoutePath } from '../../../lib/helpers/urlHelper';
import './BookAMeetingPageInfo.scss';
import { Tab, Tabs } from '../../../components/_base/Tabs';
interface IProps {
  groupData: PresenterGroupData | null;
  label: string;
  eventSettings: EventSettingsContextType;
  presenterGroups: Tab[];
  selectedGroupId: string | null;
  onTabChange: (tabId: string) => void;
  isSinglePresenterGroup: boolean;
  primaryColor?: string | null;
}

const className = 'book-a-meeting-page';
const classNameWrapper = `${className}--wrapper`;
const classNameTitle = `${className}--title`;

const BOOKABLE_STATUS_TO_CLASS_NAME: Record<string, string> = {
  [BookableStatus.JOIN_AS_PARTICIPANT]: 'booked',
  [BookableStatus.JOIN_AS_NON_PARTICIPANT]: 'available',
  [BookableStatus.UNAVAILABLE]: 'unavailable',
  [BookableStatus.BOOKABLE]: 'available'
};

const BookAMeetingPageInfo = (props: IProps): JSX.Element => {
  const { eventSettings, groupData, presenterGroups, selectedGroupId, onTabChange, isSinglePresenterGroup, primaryColor } = props;
  const history = useHistory();
  const pathPrefix = usePathPrefix();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [bookingResult, setBookingResult] = useState<RegistrationResult>(RegistrationResultType.BOOKED);
  const [selectedSlot, setSelectedSlot] = useState<MeetingSlot | null>(null);
  const [expandedBioSpeakerId, setExpandedBioSpeakerId] = useState<string | null>(null);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string | null>(null);
  const [slotStatusOverrides, setSlotStatusOverrides] = useState<Record<string, BookableStatus>>({});
  const templateProcessor = useTemplateProcessor();

  const meetingSlots = groupData?.meetingSlots;
  const speakers = groupData?.speakers;
  const eventTimeZone = groupData?.eventTimeZone;
  const groupId = selectedGroupId ?? groupData?.id ?? '';

  const computeTransformedTimeZone = (): string => {
    if (!eventSettings?.showEventTimeZone) return '';
    const timeZone = eventTimeZone || eventSettings?.timeZone || '';
    if (!timeZone) return '';
    const startDateTimeForTz = eventSettings?.startTime
      ? utcToZonedTime(eventSettings.startTime, timeZone)
      : new Date();
    const timeZoneLabel = getEventOffsetGMT(
      eventSettings?.timeZoneDisplayType as TimeZoneDisplayTypeLabels,
      eventSettings?.customTimeZoneDisplay as string,
      startDateTimeForTz,
      timeZone
    );
    return eventSettings?.timeZoneDisplayType === TimeZoneDisplayTypeLabels.Custom
      ? templateProcessor(timeZoneLabel)
      : timeZoneLabel;
  };
  const transformedTimeZone = computeTransformedTimeZone();

  const speakerList: SpeakerProfile[] =
    speakers && speakers.length > 0
      ? speakers
      : [];

  const hasMeetings = meetingSlots && meetingSlots.length > 0;

  const getEmptyListMessageBlock = (): JSX.Element => {
    return (
      <>
        <div className='font-size-14px italic px-4 py-10'>
          No meetings have been added to Book a Meeting yet.
        </div>
        <hr />
      </>
    );
  };

  const effectiveSelectedSpeakerId = selectedSpeakerId ?? speakerList[0]?.id ?? null;

  const selectedSpeaker: SpeakerProfile | null =
    effectiveSelectedSpeakerId !== null
      ? speakerList.find((s) => s.id === effectiveSelectedSpeakerId) ?? null
      : null;

  const currentTimeZone = getCurrentTimezone();

  const showLocalTime = eventTimeZone && hasMeetings && meetingSlots
    ? shouldShowLocalTime(meetingSlots[0].startDateTime, eventTimeZone)
    : false;

  const formattedSlots = useMemo((): FormattedMeetingSlot[] => {
    if (!hasMeetings || !meetingSlots || !eventTimeZone) {
      return [];
    }

    const pillDateFormat = getIsEnUsRegion()
      ? DateFnsFormats.StringifiedUSShortDay
      : DateFnsFormats.StringifiedEUShortDay;

    return meetingSlots.map((slot) => {
      const startTimeEvent = formatTimeWithUppercasePeriod(slot.startDateTime, eventTimeZone);
      const endTimeEvent = formatTimeWithUppercasePeriod(slot.endDateTime, eventTimeZone);
      const startTimeLocal = formatTimeWithUppercasePeriod(slot.startDateTime, currentTimeZone);
      const endTimeLocal = formatTimeWithUppercasePeriod(slot.endDateTime, currentTimeZone);
      const date = dateToFormattedString(slot.startDateTime, DateFnsFormats.StringifiedDayMonthNumericDateYear, eventTimeZone);
      const datePillLabel = dateToFormattedString(slot.startDateTime, pillDateFormat, eventTimeZone);

      return {
        ...slot,
        startTimeEvent,
        endTimeEvent,
        startTimeLocal,
        endTimeLocal,
        date,
        datePillLabel,
      };
    });
  }, [currentTimeZone, eventTimeZone, hasMeetings, meetingSlots]);

  const sortedDateEntries = useMemo(() => {
    const groupedSlots = formattedSlots.reduce((acc: Record<string, FormattedMeetingSlot[]>, slot) => {
      if (!acc[slot.datePillLabel]) {
        acc[slot.datePillLabel] = [];
      }
      acc[slot.datePillLabel].push(slot);
      return acc;
    }, {} as Record<string, FormattedMeetingSlot[]>);

    const getEarliestStartTime = (slots: FormattedMeetingSlot[]): number =>
      Math.min(...slots.map((slot) => new Date(slot.startDateTime).getTime()));

    return Object.entries(groupedSlots).sort(([, slotsA], [, slotsB]) =>
      getEarliestStartTime(slotsA) - getEarliestStartTime(slotsB)
    );
  }, [formattedSlots]);

  const {
    availablePillLabels,
    selectedDate,
    showAllDates,
    showDateFilter,
    visibleDateEntries,
    handleDateChange,
    handleShowAllDates,
  } = useMeetingDateSelector({ sortedDateEntries, groupId });

  if (!groupData) {
    return <div className={classNameWrapper}></div>;
  }

  const handleBookMeeting = (slotId: string) => {
    const slot = meetingSlots?.find((s: MeetingSlot) => s.id === slotId);
    if (slot) {
      setSelectedSlot(slot);
      setIsModalOpen(true);
    }
  };

  const handleConfirmBooking = (result: RegistrationResult) => {
    setIsModalOpen(false);
    setBookingResult(result);
    setIsConfirmationModalOpen(true);

    if (selectedSlot && (result === RegistrationResultType.BOOKED || result === RegistrationResultType.UNAVAILABLE)) {
      const newStatus = result === RegistrationResultType.BOOKED ? BookableStatus.JOIN_AS_PARTICIPANT : BookableStatus.UNAVAILABLE;
      setSlotStatusOverrides(prev => ({
        ...prev,
        [selectedSlot.id]: newStatus
      }));
    }
  };

  const handleCloseConfirmation = () => {
    setIsConfirmationModalOpen(false);
    setSelectedSlot(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSlot(null);
  };

  const handleJoinMeeting = (slotId: string): void => {
    if (eventSettings?.eventId) {
      const eventId = eventSettings.eventId.toString();
      // Preserve the exact Book a Meeting page the user joined from (supports multiple pages)
      history.push(
        normalizeRoutePath(`/session/${eventId}/meeting/${slotId}`, pathPrefix),
        history.location.pathname
      );
    }
  };

  const getEffectiveBookableStatus = (slot: typeof formattedSlots[0]): BookableStatus | undefined => {
    return slotStatusOverrides[slot.id] || slot.bookableStatus;
  };

  const getSlotClassName = (slot: typeof formattedSlots[0]): string => {
    const effectiveStatus = getEffectiveBookableStatus(slot);
    return effectiveStatus && BOOKABLE_STATUS_TO_CLASS_NAME[effectiveStatus]
      ? BOOKABLE_STATUS_TO_CLASS_NAME[effectiveStatus]
      : slot.status;
  };

  const appliedColor = primaryColor || PRIMARY_COLOR;
  const customStyle = { '--primary-color': appliedColor } as React.CSSProperties;

  return (
    <div className={classNameWrapper} style={customStyle}>
      <div className="book-a-meeting-header-container-wrapper">
        {!hasMeetings
          ? (
            <>
              <div className="book-a-meeting-header-container">
                <div className="book-a-meeting-header">
                  <h1 className={`${classNameTitle} book-a-meeting-page--title-no-meetings`}>{props.label}</h1>
                </div>
              </div>
              <hr />
              {getEmptyListMessageBlock()}
            </>
          )
          : (
            <>
              <div className="book-a-meeting-header-container">
                <div className="book-a-meeting-header">
                  <h1 className={classNameTitle}>{props.label}</h1>
                  <div className="availability-container">
                    <div className="eye-icon">
                      <Icon.EyeIcon />
                    </div>
                    <span className="availability-text">Unavailable Meetings Visible</span>
                  </div>
                </div>
                <div className="book-a-meeting-subtitle">
                  <p>
                    {`Schedule a private meeting with ${speakerList.length === 1 && isSinglePresenterGroup && selectedSpeaker ? selectedSpeaker.name : 'our analysts'} to discuss specific topics and get personalized insights.`}
                  </p>
                </div>
                <div className="availability-container availability-container-mobile">
                  <div className="eye-icon">
                    <Icon.EyeIcon />
                  </div>
                  <span className="availability-text">Unavailable Meetings Visible</span>
                </div>
                {!isSinglePresenterGroup && <div className='presenter-groups'>
                  <Tabs
                    tabs={presenterGroups}
                    activeTabId={selectedGroupId}
                    onTabChange={onTabChange}
                  />
                </div>}
              </div>
              {!selectedGroupId && !isSinglePresenterGroup && <div className="book-a-meeting-content presenter-selection-info">
                <span>Please select a presenter above to view available meetings.</span>
              </div>}
              {(selectedGroupId || isSinglePresenterGroup) && <div className={`book-a-meeting-content ${isSinglePresenterGroup && 'm-top-30px'}`}>
                {showDateFilter && eventTimeZone && (
                  <div className="book-a-meeting-date-filter">
                    <DateFilter
                      dates={availablePillLabels}
                      timeZone={eventTimeZone}
                      showAllDates={showAllDates}
                      selectedDate={selectedDate}
                      onSelectedDateChange={handleDateChange}
                      changeShowAllDates={handleShowAllDates}
                      isTogglePastMeetingsBtnShown={false}
                      maxVisibleDatePills={MAX_VISIBLE_DATE_PILLS}
                    />
                  </div>
                )}
                <div className="book-a-meeting-columns">
                  <div className="speaker-profile-section">
                    {speakerList.map((sp) => {
                      const isSelected = sp.id === effectiveSelectedSpeakerId;
                      const isBioExpandedForSpeaker = expandedBioSpeakerId === sp.id;

                      return (
                        <div key={sp.id}>
                          <div
                            className={`speaker-profile${isSelected ? ' speaker-profile--selected' : ''}`}
                            onClick={() => setSelectedSpeakerId(sp.id)}
                          >
                            {sp.profileImageUrl && (
                              <div className="speaker-photo">
                                <img src={sp.profileImageUrl} alt="" className="speaker-photo" />
                              </div>
                            )}
                            <div className="speaker-info">
                              <h2 className="speaker-name">{sp.name}</h2>
                              <p className="speaker-title">{sp.title}</p>
                              {sp.biography && !isBioExpandedForSpeaker && (
                                <button
                                  type="button"
                                  className="view-bio-btn-mobile"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setExpandedBioSpeakerId(sp.id);
                                  }}
                                  aria-label="View speaker biography"
                                  aria-expanded={false}
                                >
                                  View Bio +
                                </button>
                              )}
                            </div>
                          </div>
                          {sp.biography && isBioExpandedForSpeaker && (
                            <>
                              <div className="speaker-bio">
                                <p>{sp.biography}</p>
                              </div>
                              <button
                                type="button"
                                className="view-bio-btn-mobile hide-bio-active"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setExpandedBioSpeakerId(null);
                                }}
                                aria-label="Hide speaker biography"
                                aria-expanded={true}
                              >
                                Hide Bio
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="meeting-schedule-section">
                    {visibleDateEntries.length === 0
                      ? (
                        <div className="no-slots-message">
                          <p>No available meeting slots at this time.</p>
                        </div>
                      )
                      : (
                        visibleDateEntries.map(([pillLabel, slots]) => (
                          <div key={pillLabel} className="date-group">
                            <div className="date-header">
                              <span className="date-text">{slots[0].date}</span>
                              <span className="slots-text">{slots.length} Session{slots.length !== 1 ? 's' : ''}</span>
                            </div>
                            {slots.map((slot) => (
                              <div key={slot.id} className={`time-slot ${getSlotClassName(slot)}`} role="listitem">
                                <div className="slot-time">
                                  <span className="est-time" aria-label={`Event time: ${slot.startTimeEvent} to ${slot.endTimeEvent}${transformedTimeZone ? ` ${transformedTimeZone}` : ''}`}>
                                    {slot.startTimeEvent} - {slot.endTimeEvent}
                                    <span className="normal-case word-break"> {transformedTimeZone}</span>
                                  </span>
                                  {showLocalTime && (
                                    <span className="local-time" aria-label={`Your local time: ${slot.startTimeLocal} to ${slot.endTimeLocal}`}>
                                      Your local time: {slot.startTimeLocal} - {slot.endTimeLocal}
                                    </span>
                                  )}
                                  {getEffectiveBookableStatus(slot) === BookableStatus.JOIN_AS_PARTICIPANT && (
                                    <span className="booked-status" aria-label="Meeting is booked">
                                      <Icon.CheckmarkCircleIcon width="17" height="16" fill={appliedColor} />
                                      <span className="booked-text">Booked</span>
                                    </span>
                                  )}
                                </div>
                                <div className="slot-status">
                                  {(getEffectiveBookableStatus(slot) === BookableStatus.JOIN_AS_PARTICIPANT || getEffectiveBookableStatus(slot) === BookableStatus.JOIN_AS_NON_PARTICIPANT) && (
                                    <Button
                                      onClick={() => handleJoinMeeting(slot.id)}
                                      className="join-meeting-btn"
                                      label="Join"
                                      name={`join-meeting-${slot.id}`}
                                      ariaLabel={`Join meeting on ${slot.date} from ${slot.startTimeEvent} to ${slot.endTimeEvent}${transformedTimeZone ? ` ${transformedTimeZone}` : ''}`}
                                    />
                                  )}
                                  {(getEffectiveBookableStatus(slot) === BookableStatus.UNAVAILABLE || (!getEffectiveBookableStatus(slot) && slot.status === 'unavailable')) && (
                                    <span className="unavailable-status" aria-label={`Meeting slot on ${slot.date} from ${slot.startTimeEvent} to ${slot.endTimeEvent}${transformedTimeZone ? ` ${transformedTimeZone}` : ''} is unavailable`}>
                                      <span className="sr-only">Unavailable: </span>
                                      Unavailable
                                    </span>
                                  )}
                                  {(getEffectiveBookableStatus(slot) === BookableStatus.BOOKABLE || (!getEffectiveBookableStatus(slot) && slot.status !== 'unavailable')) && (
                                    <Button
                                      onClick={() => handleBookMeeting(slot.id)}
                                      className="book-meeting-btn"
                                      label="Book Meeting"
                                      name={`book-meeting-${slot.id}`}
                                      ariaLabel={`Book meeting on ${slot.date} from ${slot.startTimeEvent} to ${slot.endTimeEvent}${transformedTimeZone ? ` ${transformedTimeZone}` : ''}`}
                                    />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))
                      )}
                  </div>
                </div>
              </div>}
            </>
          )}
      </div>
      {hasMeetings && eventTimeZone && (
        <>
          <BookMeetingModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onConfirm={handleConfirmBooking}
            slot={selectedSlot}
            speakers={speakerList}
            eventTimeZone={eventTimeZone}
            eventId={eventSettings?.eventId?.toString()}
            transformedTimeZone={transformedTimeZone}
          />
          <BookingResultModal
            isOpen={isConfirmationModalOpen}
            onClose={handleCloseConfirmation}
            slot={selectedSlot}
            speakers={speakerList}
            eventTimeZone={eventTimeZone}
            showLocalTime={showLocalTime}
            result={bookingResult}
            primaryColor={primaryColor || null}
            transformedTimeZone={transformedTimeZone}
          />
        </>
      )}
    </div>
  );
};

export default BookAMeetingPageInfo;
